import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { db } from "../../config/firebase";

const COLLECTION_RECEITAS = "financeiro_receitas";

const STATUS_OPTIONS = ["PREVISTO", "RECEBIDO", "ATRASADO", "CANCELADO"];

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroReceitasPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [campoFiltro, setCampoFiltro] = useState("descricao");
  const [textoFiltro, setTextoFiltro] = useState("");

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        setClientes(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  useEffect(() => {
    if (restringirEmpresa) {
      // no-op: empresaId não é mais utilizado
    }
  }, [restringirEmpresa, empresaEscopoId]);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const snap = await getDocs(
          query(collection(db, COLLECTION_RECEITAS), where("grupoId", "==", usuario.grupoId))
        );

        let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (restringirEmpresa && empresaEscopoId) {
          lista = lista.filter((item) => String(item?.empresaId || "") === String(empresaEscopoId));
        }

        lista.sort((a, b) => String(a.vencimento || "").localeCompare(String(b.vencimento || ""), "pt-BR"));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar receitas:", e);
        setErro("Erro ao carregar receitas.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, restringirEmpresa, empresaEscopoId]);

  const camposFiltro = [
    { value: "descricao", label: "Descrição" },
    { value: "clienteOrigem", label: "Cliente" },
    { value: "categoria", label: "Categoria" },
    { value: "status", label: "Status" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;
    return registros.filter((r) => String(r?.[campoFiltro] ?? "").toLowerCase().includes(texto));
  }, [registros, campoFiltro, textoFiltro]);

  const handleExcluir = async (registro) => {
    const confirmar = window.confirm("Deseja excluir esta receita?");
    if (!confirmar) return;
    setErro("");
    setSucesso("");

    try {
      await deleteDoc(doc(db, COLLECTION_RECEITAS, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
      setSucesso("Receita excluída com sucesso.");
    } catch (e) {
      console.error("Erro ao excluir receita:", e);
      setErro("Erro ao excluir receita.");
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo="Receitas" subtitulo="Financeiro / Movimentação / Receitas">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Receitas e contas a receber</h3>
            <p>Registros de entradas financeiras com controle de vencimento e recebimento.</p>
          </div>
          <div className="pc-table-header-actions">
            <button type="button" className="pc-btn-primario" onClick={() => navigate("/financeiro/receitas/nova")}>
              + Adicionar receita
            </button>
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar receitas..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          <table className="pc-table pc-table-expandable">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Cliente</th>
                <th>Categoria</th>
                <th>Vencimento</th>
                <th>Valor recebido</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={7} style={{ padding: 16 }}>Carregando receitas...</td></tr>
              ) : filtrados.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16 }}>Nenhuma receita encontrada.</td></tr>
              ) : (
                filtrados.map((item) => {
                  const aberto = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>{item.descricao || "-"}</td>
                        <td>{item.clienteOrigem || "-"}</td>
                        <td>{item.categoria || "-"}</td>
                        <td>{item.vencimento || "-"}</td>
                        <td>R$ {formatMoney(item.valorRecebido)}</td>
                        <td>{item.status || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                            >
                              {aberto ? "Ocultar" : "Detalhes"}
                            </button>
                            <button type="button" className="pc-btn-secundario" onClick={() => navigate(`/financeiro/receitas/editar/${item.id}`)}>
                              Editar
                            </button>
                            <button type="button" className="pc-btn-perigo" onClick={() => handleExcluir(item)}>
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Empresa</div>
                                  <div className="pc-expand-value">{item.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Competência</div>
                                  <div className="pc-expand-value">{item.competencia || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Data recebimento</div>
                                  <div className="pc-expand-value">{item.dataRecebimento || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Valor previsto</div>
                                  <div className="pc-expand-value">R$ {formatMoney(item.valorPrevisto || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Desconto</div>
                                  <div className="pc-expand-value">R$ {formatMoney(item.desconto || 0)}</div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Observações</div>
                                  <div className="pc-expand-value">{item.observacoes || "-"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
